import { BaseService } from '../base';
import { authenticationService } from '../authentication';
import {
  downloadBlob,
  filenameFromResponse,
} from '@/shared/utils/download-blob.util';
import type {
  AutomaticEmissionResponse,
  EmissionControlDetailsResponse,
  EmissionControlListResponse,
  GetIntegrationsParams,
  GetPolicyBatchParams,
  ManualEmissionResponse,
  PolicyBatchBulkResult,
  PolicyBatchFacets,
  PolicyBatchItem,
  PolicyBatchKey,
  PolicyBatchListResponse,
  PolicyBatchPatch,
  PolicyBatchReleaseCheck,
  PolicyBatchSummary,
  PolicyBatchUploadResponse,
} from './types';

export type {
  AutomaticEmissionOutcome,
  AutomaticEmissionResponse,
  EmissionControl,
  EmissionControlDetailsResponse,
  EmissionControlListResponse,
  GetIntegrationsParams,
  GetPolicyBatchParams,
  IntegrationHistory,
  ManualEmissionResponse,
  PolicyBatchBulkError,
  PolicyBatchBulkResult,
  PolicyBatchFacets,
  PolicyBatchItem,
  PolicyBatchKey,
  PolicyBatchListResponse,
  PolicyBatchPatch,
  PolicyBatchReleaseBlock,
  PolicyBatchReleaseBlockReason,
  PolicyBatchReleaseCheck,
  PolicyBatchPoolSummary,
  PolicyBatchSummary,
  PolicyBatchUploadError,
  PolicyBatchUploadResponse,
} from './types';

class IntegrationsService extends BaseService {
  private readonly customBaseUrl: string;

  constructor() {
    super();
    this.customBaseUrl = import.meta.env.VITE_API_URL;
  }

  async listIntegrations(params: GetIntegrationsParams = {}): Promise<{
    success: boolean;
    data?: EmissionControlListResponse;
    error?: string;
  }> {
    const queryParams = new URLSearchParams();

    if (params.pageNumber)
      queryParams.append('pageNumber', params.pageNumber.toString());
    if (params.pageSize)
      queryParams.append('pageSize', params.pageSize.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.step) queryParams.append('step', params.step);
    if (params.insuremoPolicyId)
      queryParams.append('insuremoPolicyId', params.insuremoPolicyId);
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const path = `/portal/v1/policies/integrations${queryString ? `?${queryString}` : ''}`;

    const response = await this.get(path, {}, this.customBaseUrl);
    return this.handleJsonResponse<EmissionControlListResponse>(response);
  }

  async getIntegrationDetails(policyId: string): Promise<{
    success: boolean;
    data?: EmissionControlDetailsResponse;
    error?: string;
  }> {
    const response = await this.get(
      `/portal/v1/policies/integrations/${encodeURIComponent(policyId)}`,
      {},
      this.customBaseUrl
    );
    return this.handleJsonResponse<EmissionControlDetailsResponse>(response);
  }

  async reprocessIntegration(policyId: string): Promise<{
    success: boolean;
    data?: { message: string };
    error?: string;
  }> {
    const response = await this.post(
      `/portal/v1/policies/integrations/${encodeURIComponent(policyId)}/reprocess`,
      undefined,
      this.customBaseUrl
    );
    return this.handleJsonResponse<{ message: string }>(response);
  }

  /**
   * Emissão manual: informa ao BFF o nº da apólice já emitida no i4Pro.
   *
   * NÃO lança em erro de negócio — devolve o envelope para o modal exibir a
   * mensagem inline (apólice inexistente, de outro produto, já vinculada, i4Pro
   * indisponível) sem se fechar e sem perder o que o admin digitou.
   */
  /**
   * Multipart: o nº da apólice e o PDF dela (obrigatório fora do RD — a Api
   * recusa sem ele). Mesmo padrão de envio do `uploadPolicyBatch`.
   */
  async manualEmission(
    policyId: string,
    i4proPolicyNo: string,
    policyPdf?: File | null
  ): Promise<{
    success: boolean;
    data?: ManualEmissionResponse;
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('i4proPolicyNo', i4proPolicyNo);
    if (policyPdf) formData.append('policyPdf', policyPdf);

    const accessToken = await authenticationService.getAuthSession();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken.toString()}`;
    }

    const response = await fetch(
      `${this.customBaseUrl}/portal/v1/policies/integrations/${encodeURIComponent(policyId)}/manual-emission`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );
    return this.handleJsonResponse<ManualEmissionResponse>(response);
  }

  /**
   * Baixa o kit de implantação do Vida PME (zip com a proposta e a carta
   * assinadas e a planilha das vidas de cada subestipulante). O nome do arquivo
   * vem do `Content-Disposition`. NÃO lança em erro do BFF: devolve a mensagem
   * para o toast.
   */
  async downloadImplantationKit(policyId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const response = await this.get(
        `/portal/v1/policies/integrations/${encodeURIComponent(policyId)}/implantation-kit`,
        {},
        this.customBaseUrl
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        return {
          success: false,
          error:
            body?.detail ??
            `Não foi possível baixar o kit de implantação (${response.status}).`,
        };
      }

      downloadBlob(
        await response.blob(),
        filenameFromResponse(response, 'kit-de-implantacao.zip')
      );
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Não foi possível baixar o kit de implantação.',
      };
    }
  }

  /**
   * Reenvia a Operações o aviso de emissão manual com o pacote de emissão (Vida
   * PME). O BFF responde na hora e monta/envia em segundo plano — o desfecho
   * aparece no histórico. NÃO lança em erro de negócio.
   */
  async resendImplantationKit(policyId: string): Promise<{
    success: boolean;
    data?: { message: string };
    error?: string;
  }> {
    const response = await this.post(
      `/portal/v1/policies/integrations/${encodeURIComponent(policyId)}/implantation-kit/resend`,
      {},
      this.customBaseUrl
    );
    return this.handleJsonResponse<{ message: string }>(response);
  }

  /**
   * Tenta emitir agora, pela integração, uma proposta que aguardava emissão
   * manual. NÃO lança em erro de negócio — o modal exibe a mensagem inline.
   * Uma tentativa que falha na emissão ainda volta `success: true`; o desfecho
   * vem em `data.outcome`.
   */
  async releaseToAutomaticEmission(policyId: string): Promise<{
    success: boolean;
    data?: AutomaticEmissionResponse;
    error?: string;
  }> {
    const response = await this.post(
      `/portal/v1/policies/integrations/${encodeURIComponent(policyId)}/automatic-emission`,
      undefined,
      this.customBaseUrl
    );
    return this.handleJsonResponse<AutomaticEmissionResponse>(response);
  }

  async listPolicyBatch(params: GetPolicyBatchParams = {}): Promise<{
    success: boolean;
    data?: PolicyBatchListResponse;
    error?: string;
  }> {
    const queryParams = new URLSearchParams();
    if (params.lobId) queryParams.append('lobId', params.lobId.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.channelCode)
      queryParams.append('channelCode', params.channelCode.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.pageNumber)
      queryParams.append('pageNumber', params.pageNumber.toString());
    if (params.pageSize)
      queryParams.append('pageSize', params.pageSize.toString());

    const queryString = queryParams.toString();
    const path = `/portal/v1/policies/batch${queryString ? `?${queryString}` : ''}`;

    const response = await this.get(path, {}, this.customBaseUrl);
    return this.handleJsonResponse<PolicyBatchListResponse>(response);
  }

  async getPolicyBatchFacets(): Promise<{
    success: boolean;
    data?: PolicyBatchFacets;
    error?: string;
  }> {
    const response = await this.get(
      '/portal/v1/policies/batch/facets',
      {},
      this.customBaseUrl
    );
    return this.handleJsonResponse<PolicyBatchFacets>(response);
  }

  async getPolicyBatchSummary(): Promise<{
    success: boolean;
    data?: PolicyBatchSummary;
    error?: string;
  }> {
    const response = await this.get(
      '/portal/v1/policies/batch/summary',
      {},
      this.customBaseUrl
    );
    return this.handleJsonResponse<PolicyBatchSummary>(response);
  }

  async updatePolicyBatchItem(
    key: PolicyBatchKey,
    patch: PolicyBatchPatch
  ): Promise<{ success: boolean; data?: PolicyBatchItem; error?: string }> {
    const response = await this.put(
      `/portal/v1/policies/batch/${key.lobId}/${key.policyNumber}`,
      patch,
      this.customBaseUrl
    );
    return this.handleJsonResponse<PolicyBatchItem>(response);
  }

  async deletePolicyBatchItem(
    key: PolicyBatchKey
  ): Promise<{ success: boolean; data?: { message: string }; error?: string }> {
    const response = await this.delete(
      `/portal/v1/policies/batch/${key.lobId}/${key.policyNumber}`,
      this.customBaseUrl
    );
    return this.handleJsonResponse<{ message: string }>(response);
  }

  /**
   * Pergunta ao BFF quais dos números selecionados podem ir para `Disponivel`. Não altera
   * nada — a tela usa o resultado para confirmar uma segunda vez.
   */
  async checkPolicyBatchRelease(items: PolicyBatchKey[]): Promise<{
    success: boolean;
    data?: PolicyBatchReleaseCheck;
    error?: string;
  }> {
    const response = await this.post(
      '/portal/v1/policies/batch/bulk/release-check',
      { items },
      this.customBaseUrl
    );
    return this.handleJsonResponse<PolicyBatchReleaseCheck>(response);
  }

  /** `BaseService` não expõe PATCH — daí o `fetch` direto, mesmo caminho do upload. */
  async bulkUpdatePolicyBatch(
    items: PolicyBatchKey[],
    patch: PolicyBatchPatch
  ): Promise<{
    success: boolean;
    data?: PolicyBatchBulkResult;
    error?: string;
  }> {
    const accessToken = await authenticationService.getAuthSession();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken.toString()}`;
    }

    const response = await fetch(
      `${this.customBaseUrl}/portal/v1/policies/batch/bulk`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ items, patch }),
      }
    );

    return this.handleJsonResponse<PolicyBatchBulkResult>(response);
  }

  async uploadPolicyBatch(file: File): Promise<{
    success: boolean;
    data?: PolicyBatchUploadResponse;
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const accessToken = await authenticationService.getAuthSession();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken.toString()}`;
    }

    const response = await fetch(
      `${this.customBaseUrl}/portal/v1/policies/batch/upload`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );

    return this.handleJsonResponse<PolicyBatchUploadResponse>(response);
  }
}

export const integrationsService = new IntegrationsService();
